
import { FEATURE_KIND } from '../../model/enums';
import { WizMathUtils } from '@wizeoo/akm-core-lib';
import { Feature, TranslatedEmb, VisionStep } from '@wizeoo/akm-message-lib';
import { ViewData } from '../../model/view.data';
import { BaselineEngine, VisionVO, entity, field } from '@wizeoo/akm-compute-lib';
import { Model } from '../../model/model';
import { TabVO } from '../workingzone/tab.vo';

// TODO TODO alimenter les éléments manquants depuis allkeyme

/** VO Feature */
@entity('wizeoo.allkeyme.user.message.Feature', 'BU')
export class FeatureVO {
  /**
   * Le VO source provenant du back
   */
  private _source: Feature;

  protected _model: Model = null;
  public get model(): Model {
    return this._model as Model;
  }

  constructor(model: Model, pSource?: Feature) {
    this._model = model;
    this._source = null;
    if (pSource) {
      this._source = pSource;
    } else {
      this._source = new Feature();
      this._source.uid = WizMathUtils.createUUID(); // génération du UID si nouveau
    }
  }

  public set source(value: Feature) {
    this._source = value;
  }
  public get source(): Feature {
    return this._source;
  }

  public set uid(uid: string) {
    this._source.uid = uid;
  }
  public get uid(): string {
    return this._source.uid;
  }

  // Id technique
  public get id(): number {
    return this._source.id;
  }
  public set id(value: number) {
    this._source.id = value;
  }

  public set domain(domain: string) {
    this._source.domain = domain;
  }
  public get domain(): string {
    return this._source.domain;
  }

  public get businessUnitCode(): string {
    // code de la BU
    return this._source.businessUnitCode;
  }
  public set businessUnitCode(value: string) {
    this._source.businessUnitCode = value;
  }

  @field('string')
  public set code(code: string) {
    this._source.code = code;
  }
  public get code(): string {
    return this._source.code;
  }

  @field('TranslatedEmb')
  public set label(value: TranslatedEmb) {
    this._source.label = value;
  }
  public get label(): TranslatedEmb {
    return this._source.label;
  }

  @field('boolean')
  public get deprecated(): boolean {
    return this._source.deprecated;
  }
  public set deprecated(value: boolean) {
    this._source.deprecated = value;
  }

  @field('boolean')
  public get deleted(): boolean {
    return this._source.deleted;
  }
  public set deleted(value: boolean) {
    this._source.deleted = value;
  }

  @field('string')
  public set perimeter(value: string) {
    this._source.perimeter = value;
  }
  public get perimeter(): string {
    return this._source.perimeter;
  }

  public get dynamic(): boolean {
    return this._source.dynamic;
  }
  public set dynamic(value: boolean) {
    this._source.dynamic = value;
  }

  public set kind(kind: string) {
    this._source.kind = kind;
  }
  public get kind(): string {
    return this._source.kind;
  }

  public set visionCode(visionCode: string) {
    this._source.visionCode = visionCode;
  }

  public get visionCode(): string {
    return this._source.visionCode;
  }

  /* ################################################
                    Accesseurs composites
  ################################################ */

  public get exist(): boolean {
    // l'objet existe en BDD (id)
    return this.id > 0;
  }

  /** la feature à au moins un rôle associé */
  public get withRole(): boolean {
    return true; // TODO TODO FeatureEngine.isUsedFeature(this);
  }


  /** clé d'unicité dans l'arbre */
  public get uniqueKey(): string {
    return this.visionCode + ViewData.KEY_SEPARATOR + this.code;
  }

  public isFakeLevel: boolean = false; // ce n'est pas une vraie feature
  public headerFeatureLabel: string = null; // libellé pour les 3 features porteuses (tech, dyna, data)

  /**
   * Accès au libellé traduit
   * Retourne le code si il n'y a pas de libellé traduit
   */

  public get trsLabel(): string {
    let result: string = '';
    if (this.headerFeatureLabel) {
      // traduction des entêtes d'arbre
      result = this.model.localeEngine.translateKey(this.headerFeatureLabel);
    } else {
         
      result = this.model.localeEngine.translate(this.label, this.code); // prise par défaut sur la feature... mais c'est pas updatable...

      // console.log('kind', this.kind);
      switch(this.kind) {
        case FEATURE_KIND.VISION:
          if (this.relatedVision) {
            result = this.model.visionEngine.getVisionLabel(this.relatedVision);
          }
          break;
        case FEATURE_KIND.VISION_STEP: // un step
          const codeParts: string[] = this.code.split(ViewData.KEY_SEPARATOR);
          let stepCode: string = codeParts[codeParts.length - 1];
          if (this.relatedVision) {
            const step : VisionStep = BaselineEngine.getVisionStepByLevel(this.relatedVision, Number(codeParts[codeParts.length - 1]));
            // const step: VisionStepVO = this.relatedVision.getVisionStepByLevel(Number(codeParts[codeParts.length - 1]));
            if (step) {
              stepCode = this.model.visionEngine.getVisionStepLabel(this.relatedVision, step, true);
              // stepCode = step.levelAndLabel;
            }
          }
          result = stepCode;
          break;
        case FEATURE_KIND.TAB:
          const tab: TabVO = this.model.tabEngine.getTabByCode(this.code);
          if(!!tab) {
            result = tab.trsLabel;
          }
          break;
        default:
          // console.log('feature default lbl', this.code, this.kind, result);
          break;    
      }
    }
    // console.log('feat lbl', this.code, this.kind, result);
    return result;
  }
  /** vision liée, pour prise du label */
  public get relatedVision(): VisionVO {
    if (this.kind === FEATURE_KIND.TAB || this.kind === FEATURE_KIND.VISION || this.kind === FEATURE_KIND.VISION_STEP) {
      const visioncode: string = this.code.split(ViewData.KEY_SEPARATOR)[0];
      return this.model.visionEngine.getVision(visioncode);
    } else {
      return null;
    }
  }

  /**
   * code parent servant à la hiérarchisation des features
   */
  private _parentCode: string;
  public get parentCode(): string {
    return this._parentCode;
  }

  public set parentCode(parentCode: string) {
    this._parentCode = parentCode;
  }

  /** organisation de l'ordre, level 0 pour tout le monde, level xx pour les steps */
  public get order(): number {
    return this.kind === FEATURE_KIND.VISION_STEP ? Number(this.code.split(ViewData.KEY_SEPARATOR)[1]) : 0;
  }
}
